/**
 * Quando c'e' la barra in basso, quale voce e' accesa, e in che ordine stanno le schede. Stava dentro
 * `AppShell.tsx`; e' qui dal 2026-09-27 perche' la regola di visibilita' ha delle eccezioni e un'eccezione si prova
 * con un test, non con la lettura di un componente che nessun banco monta
 * (`tests/tabbar-sempre.test.ts`, `tests/tabbar-nella-sezione-tornei.test.ts`).
 */

/**
 * Il nome della variabile CSS che porta l'ALTEZZA VERA della barra, in px, finche' la barra e' montata (la scrive
 * `TabBar.tsx` misurandosi).
 *
 * 🔴 Perche' esiste (I-383, Luca: il tavolo di backgammon ha la barra): una schermata `fixed` a tutto schermo — il tavolo
 * orizzontale di `BackgammonMatch` — non sta nel flusso sopra la barra, quindi non puo' «lasciarle il posto» da sola, e
 * deve sapere di quanto accorciarsi. Si MISURA la barra invece di riscriverne la formula (44 + 4 + inset): una seconda
 * copia dell'altezza invecchia in silenzio il giorno che la barra cambia. ⚠️ Senza barra montata la variabile non c'e', e
 * chi la legge usa il ripiego `0px` (la schermata torna a tutto schermo, com'era): e' il caso dei banchi che montano la
 * schermata da sola e delle eccezioni di questo file.
 */
export const ALTEZZA_TABBAR_VAR = '--altezza-tabbar';

/**
 * Il nome della variabile CSS che porta l'INGOMBRO IN BASSO, in px: quanto sta occupato in fondo allo schermo da chi disegna le barre (oggi la sola barra delle schede, e vale come
 * `ALTEZZA_TABBAR_VAR`; la v3 (tma-ui-orcr, I-973/I-975) aggiunge la barra del torneo attivo e toglie la barra in match ed esiti, e cambiera' il VALORE scritto qui, non chi lo legge).
 * La scrive chi disegna le barre (`TabBar.tsx`), la legge il bottone flottante dei minigiochi per il punto di partenza e per il limite in basso del trascinamento
 * (`BottoneMinigiochi.tsx`): MAI l'altezza fissa della barra (richiesta di tma-orc, 20/10). Assente = nessuna barra: chi la legge usa il ripiego sull'inset di sicurezza.
 * ⚠️ Non sostituisce `ALTEZZA_TABBAR_VAR`: quella dice l'altezza della SOLA barra delle schede, e il tavolo del backgammon e il tabellone del burraco continuano a leggere quella.
 */
export const INGOMBRO_BASSO_VAR = '--ingombro-basso';

/**
 * Il percorso come lo confrontano TUTTE le regole di questo file: senza slash finali (la radice resta `/`) e in
 * minuscolo.
 *
 * 🔴 Perche' esiste: `/news/`, `/friends/`, `/settings/` mostrano la pagina giusta (React Router li risolve) ma il
 * confronto era ESATTO, quindi la barra spariva [M consegna-tornei-orcb, collaudo-c, 04/10 22:17-22:21Z]. E il
 * router non distingue le maiuscole (`caseSensitive` e' falso di default): `/News` mostra l'elenco, e un confronto
 * che le distingue direbbe «non e' una scheda» su una pagina che lo e'.
 * ⚠️ Non toglie la query ne' l'hash: `location.pathname` non li porta, e chi passa un URL intero sta sbagliando funzione.
 */
export function normalizzaPercorso(pathname: string): string {
  const senzaSlashFinali = pathname.replace(/\/+$/, '');
  return (senzaSlashFinali === '' ? '/' : senzaSlashFinali).toLowerCase();
}

/**
 * `percorso` e' `prefisso` o sta sotto di lui, CONFRONTANDO PER SEGMENTO: `/newsletter` non sta sotto `/news`
 * (un `startsWith` nudo lo direbbe). Entrambi gia' normalizzati.
 */
export function staSotto(percorso: string, prefisso: string): boolean {
  return percorso === prefisso || percorso.startsWith(`${prefisso}/`);
}

/**
 * L'ordine delle schede in basso, che è anche l'ordine dello SPAZIO: passando
 * da News (0) a Impostazioni (3) ci si sposta verso destra, quindi il contenuto entra
 * da destra. Deve restare allineato a `TabBar.tsx` — se qui e lì divergono, le
 * animazioni vanno dalla parte sbagliata e l'effetto è peggiore del non averle.
 *
 * Il Profilo (ultima voce) è ESCLUSO di proposito: lì il banner si stringe già,
 * e quel movimento è l'animazione di quella transizione. Aggiungerne una
 * seconda sotto darebbe due cose in moto per lo stesso gesto.
 *
 * 🔴 La quarta voce e' `/settings`, non piu' `/inbox`, dal 2026-10-03 (I-318, Luca: «Al posto dell'icona inbox,
 * mettiamo icona settings»). `/inbox` e' una rotta valida raggiunta da Impostazioni → Comunicazione: dal 2026-10-04
 * (I-382) ha la barra, con Impostazioni accesa (v. `voceAccesa`).
 */
export const TAB_ORDER = ['/news', '/friends', '/', '/settings', '/profile'] as const;

/**
 * La posizione della SCHEDA nello spazio (v. `TAB_ORDER`), o -1 se il percorso non e' una delle cinque schede.
 *
 * Confronto esatto sul percorso NORMALIZZATO e non `startsWith`: `/news/abc` è il DETTAGLIO di un articolo, non la
 * scheda News — entrarci non è uno spostamento laterale, e l'animazione di ingresso direbbe il falso. Dal 2026-10-04
 * la barra c'e' anche li', ma lo scivolamento laterale resta delle sole schede.
 */
export function tabIndex(pathname: string): number {
  return TAB_ORDER.indexOf(normalizzaPercorso(pathname) as (typeof TAB_ORDER)[number]);
}

/** Le chiavi delle voci della barra (`TabBar.tsx`, `data-tour="tab-<chiave>"`). */
export type VoceBarra = 'news' | 'friends' | 'hub' | 'settings' | 'profile';

/**
 * Quale voce e' ACCESA, per prefisso. Dal 2026-10-04 (I-382) la barra c'e' anche nelle sotto-schermate, e una voce
 * accesa dice «sei in questa parte dell'app»:
 *  · News → `/news` e tutto cio' che ci sta sotto (articoli, tag, preferenze);
 *  · Amici → `/friends/*` (bloccati, notifiche, messaggi);
 *  · Impostazioni → `/settings/*`, `/inbox` (ci si arriva da Impostazioni → Comunicazione) e il ritorno dal
 *    collegamento di un account (`/auth/link-callback`, dove si e' partiti da Impostazioni → Account collegati);
 *  · Profilo → SOLO `/profile` esatto: e' il proprio profilo, la scheda;
 *  · Amici → anche `/profile/*`: e' il profilo di UN ALTRO giocatore (`UserProfile`, `/profile/:id`, ci si arriva da
 *    una riga di amici, da un risultato di ricerca, da una conversazione), e accendere «Profilo» farebbe credere che sia
 *    il proprio (obiezione del gate Codex sull'ADR-0196, accolta da tma-orcb). Sotto `/profile/` non c'e' nessuna
 *    sotto-schermata del proprio profilo (`router.tsx`: l'unica rotta e' `profile/:id`; le impostazioni stanno sotto
 *    `/settings`): se un giorno ce ne sara' una va nell'elenco QUI, prima di `/profile`;
 *  · TUTTO IL RESTO → Hub: la Hub e' la porta delle sezioni di gioco e dei tornei (Luca, 27/09: «se un giocatore
 *    premi ancora hub ritorna nell'hub»). Il `*` del router rimanda a `/`, quindi non esiste un percorso senza voce.
 *
 * ⚠️ Per SEGMENTO, non `startsWith`: `/newsletter` non e' News. ⚠️ Non e' la regola di `NavLink` (la Hub ha `end`, e
 * non sarebbe accesa su `/limulo/...`): `TabBar` guida le classi da QUI, cosi' c'e' una sola definizione.
 * ⚠️ L'ORDINE conta: vince la prima riga che combacia (`/profile` esatto prima di `/profile/*`).
 */
export const VOCE_DEL_PREFISSO: ReadonlyArray<
  readonly [prefisso: string, voce: VoceBarra, soloEsatto?: true]
> = [
  ['/news', 'news'],
  ['/friends', 'friends'],
  ['/settings', 'settings'],
  ['/inbox', 'settings'],
  ['/auth/link-callback', 'settings'],
  ['/profile', 'profile', true],
  ['/profile', 'friends'],
];

export function voceAccesa(pathname: string): VoceBarra {
  const percorso = normalizzaPercorso(pathname);
  const riga = VOCE_DEL_PREFISSO.find(([prefisso, , soloEsatto]) =>
    soloEsatto ? percorso === prefisso : staSotto(percorso, prefisso),
  );
  return riga?.[1] ?? 'hub';
}

/**
 * 🔴 L'ELENCO DELLE ECCEZIONI: le sole schermate della shell autenticata SENZA barra in basso.
 *
 * Luca, 2026-10-04 ~22:21Z (I-382), testuale: «io voglio che la bottom bar si veda SEMPRE, anche dentro le
 * sezioni, è importante.» Nasce da I-381: su stg-tma (iPhone 15 Pro Max) dopo qualche sezione la barra «scompare e non
 * torna più». ⇒ La regola e' «barra ovunque»; chi vuole togliere la barra da una schermata la AGGIUNGE QUI, col perche',
 * e qui si legge la lista intera. Una schermata che non e' nell'elenco ha la barra.
 *
 * Ogni voce e' un percorso e tutto cio' che gli sta sotto (per segmento, normalizzato).
 *
 * ✅ LA SCELTA DI LUCA (I-383, ~22:26Z, via flotta): i FLUSSI A PASSI con la loro barra d'azione restano SENZA barra
 * («(a)»); il TAVOLO di una partita viva ha la barra («(b)», contro il consiglio di tma-orcb): `/backgammon/match` NON
 * e' qui. L'ADR che supera §13 di ADR-0038 (richiamato da ADR-0190) e' di tma-orcb.
 *
 * Non sono qui, perche' non passano da questa funzione: l'editor avatar (`/avatars/editor`, FUORI dalla shell,
 * `router.tsx`), l'onboarding (un ramo di `AppShell` che non monta la barra) e `/auth/callback` (rotta pubblica).
 */
export interface EccezioneBarra {
  /** Il percorso (normalizzato) e tutto cio' che gli sta sotto. */
  readonly percorso: string;
  /** Il perche', in una riga: e' quello che si rilegge quando qualcuno chiede «perche' qui no?». */
  readonly perche: string;
}

export const BARRA_ECCEZIONI: readonly EccezioneBarra[] = [
  {
    percorso: '/avatars/new/assistant',
    perche:
      'il FLUSSO A PASSI con la sua barra d\'azione in basso (un tasto per passo, `useMainButton`): due strisce in ' +
      'fondo si contendono il pollice, ed e\' il motivo storico del §13 di ADR-0038. Scelta di Luca, I-383.',
  },
];

/** L'eccezione che copre il percorso, o `undefined` se la barra c'e'. */
export function eccezioneBarra(pathname: string): EccezioneBarra | undefined {
  const percorso = normalizzaPercorso(pathname);
  return BARRA_ECCEZIONI.find((e) => staSotto(percorso, e.percorso));
}

/**
 * La barra di navigazione c'e' SU OGNI SCHERMATA DELLA SHELL, tranne quelle di `BARRA_ECCEZIONI`.
 *
 * 🔴 **Dal 2026-10-04 (I-382) la regola e' questa**, per decisione di Luca (testuale in `BARRA_ECCEZIONI`). Prima la
 * barra esisteva solo sulle cinque schede e nella sezione tornei: dentro una sottopagina (un articolo, il profilo di un
 * altro giocatore, le Impostazioni) spariva, e «non torna piu'» era il sintomo di I-381. Era la regola del restyle di
 * CriGio (#146, 06/09) e di ADR-0038 §13 («da dentro un contenuto la strada e' indietro, non di lato», piu' la lite con
 * la barra d'azione della schermata): la supera l'ADR proposed di tma-orcb, per decisione di Luca.
 *
 * La sezione tornei (Luca, 27/09: la barra resta in modo che «premi ancora hub» si torni alla Hub) non e' piu' un caso a
 * parte: e' un caso della regola.
 * ⚠️ Cosa NON cambia: `useBackButton` tiene il tasto indietro di Telegram legato alla cronologia, e dentro un contenuto
 * resta il gesto per uscire. La barra e' una SECONDA strada, non la sostituisce.
 * ⚠️ La barra d'azione della schermata (`MainCta`, `LimuloRegistrationActions`) sta SOPRA la barra di navigazione.
 */
export function showsTabBar(pathname: string): boolean {
  return eccezioneBarra(pathname) === undefined;
}
